import * as React from 'react';
import { AuditLogModal } from '@web-config-app/core-react-ui';
import {
  AnnotatedJsonSchema,
  EnvironmentKey,
  transformAuditLogData,
  TreeNode,
} from '@web-config-app/core';
import { useFetchAuditLog } from '@web-config-app/core-react';
import { getQueryParamsFromUrl } from '@web-config-app/core-react/src/utilities/get-query-params-from-url/get-query-params-from-url.util';

/**
 * Container component for displaying audit log data for an entity
 *
 * This component fetches audit log data from the API and transforms it into a format suitable for display in the AuditLogModal component.
 *
 * @param {Object} props - Component props
 * @param {string} props.entityType - The type of entity to display audit log data for
 * @param {string} props.entityInstanceId - The ID of the entity to display audit log data for
 * @returns {React.ReactElement} The rendered AuditLogModal component with transformed audit log data
 */
export const EntityAuditLogContainer = ({
  entityType,
  domainEntityType,
  entityInstanceId,
  environmentKey,
  entityTreeNodeMap,
  rootSchema,
}: {
  entityType: string;
  /** Domain entity type for X-League-Config-Entity-Type / X-League-Config-Entity-Action header (e.g. config-activity) */
  domainEntityType: string;
  entityInstanceId: string;
  environmentKey: EnvironmentKey;
  entityTreeNodeMap?: Map<string, TreeNode>;
  rootSchema?: AnnotatedJsonSchema;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [offset, setOffset] = React.useState('0');
  const pageSize = '20';

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);

      // Reset the pagination offset when the modal is closed so that the audit log starts from the first page on the next open.
      if (!open) {
        setOffset('0');
      }
    },
    [setIsOpen, setOffset],
  );

  const loadPage = React.useCallback((url?: string) => {
    if (!url) return;
    const params = getQueryParamsFromUrl(url);
    setOffset(params['page[offset]'] || '0');
  }, []);
  const {
    data: auditLogData,
    isError,
    isLoading,
  } = useFetchAuditLog({
    environmentKey,
    instanceId: entityInstanceId,
    domainEntityType,
    enabled: isOpen,
    pageOffset: offset,
    pageSize,
  });

  const transformedData = React.useMemo(
    () => transformAuditLogData(auditLogData),
    [auditLogData],
  );

  return (
    <AuditLogModal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      data={transformedData}
      entityType={entityType}
      isError={isError}
      isLoading={isLoading}
      isEmpty={!isLoading && !isError && transformedData.length === 0}
      entityTreeNodeMap={entityTreeNodeMap}
      schemaForAuditLogLabels={rootSchema}
      pagination={auditLogData?.links}
      loadPage={loadPage}
    />
  );
};
